import React, {useState, useEffect} from 'react'
import login from '../../services/auth.service'
import {useForm} from "react-hook-form"
import {useEyeconxStore} from "../../store/eyeconxStore"
import {useNavigate, Link} from "react-router-dom"
import {toAbsoluteUrl} from "../helpers";

function Login() {
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors, isSubmitting},
    } = useForm();

    const [message, setMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const setToken = useEyeconxStore((state) => state.setToken);
    const setUser = useEyeconxStore((state) => state.setUser);
    const setIsAuthenticate = useEyeconxStore((state) => state.setIsAuthenticate);
    const logout = useEyeconxStore(state => state.logout);
    const user = useEyeconxStore((state) => state.user);
    const token = useEyeconxStore((state) => state.token);
    const isAuthenticated = useEyeconxStore((state) => state.isAuthenticated);

    const navigate = useNavigate();

    // Load saved credentials on component mount
    useEffect(() => {
        const savedCredentials = localStorage.getItem('rememberedCredentials');
        if (savedCredentials) {
            const {username, password} = JSON.parse(savedCredentials);
            setValue('username', username);
            setValue('password', password);
            setRememberMe(true);
        }
    }, [setValue]);

    const onSubmit = async (data) => {
        setMessage(null);
        try {
            const response = await login(data.username, data.password).then((res) => {
                if (res.data.statusCode === 200) {
                    const user = {
                        ID: res.data.uId,
                        name: res.data.username,
                        role: res.data.role,
                        clinicId: res.data.clinicId,
                        authorization: 'Bearer ' + res.data.token,
                        clinicType: res.data.clinicType,
                    };

                    setToken(res.data.token);
                    setUser(user);
                    setIsAuthenticate(true);

                    // Save credentials if "Remember Me" is checked
                    if (rememberMe) {
                        localStorage.setItem('rememberedCredentials', JSON.stringify({
                            username: data.username,
                            password: data.password
                        }));
                    } else {
                        localStorage.removeItem('rememberedCredentials');
                    }

                    localStorage.removeItem('notifications');
                }
            });
        } catch (error) {
            console.error(error);
            if (error?.response) {
                setMessage(error.response.data.error || "Login failed. Please try again.");
            } else {
                setMessage("Network error. Please check your connection.");
            }
        }
    };

    useEffect(() => {
        if (isAuthenticated && token != null) {
            if (user.clinicType === "EYECONX") {
                navigate("/dashboard")
            } else if (user.clinicType === "SMS_GATEWAY") {
                navigate("/sms-dashboard")
            } else {
                logout()
                navigate('/login')
            }
        }
    }, [user])

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-90 py-5">
            <div className="card shadow-lg w-100 w-md-500px">
                <div className="card-body p-10 p-lg-15">
                    {/* Logo */}
                    <div className="text-center mb-10">
                        <img
                            src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                            alt="Logo"
                            className="h-50px mb-5"
                        />
                        <h1 className="text-dark fw-bolder mb-3">Sign In</h1>
                    </div>

                    {/* Error Message */}
                    {message && (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center p-5 mb-10">
                            <div className="d-flex flex-column">
                                <span><i className="bi bi-exclamation-triangle"></i> {message}</span>
                            </div>
                        </div>
                    )}

                    {/* Login Form */}
                    <form className="form w-100" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="fv-row mb-8">
                            <label className="form-label fs-6 fw-bolder text-dark">Username</label>
                            <input
                                type="text"
                                className={`form-control form-control-lg form-control-solid ${errors.username ? 'is-invalid' : ''}`}
                                placeholder="Enter your username"
                                {...register("username", {required: "Username is required"})}
                            />
                            {errors.username && (
                                <div className="invalid-feedback">{errors.username.message}</div>
                            )}
                        </div>

                        <div className="fv-row mb-5">
                            <label className="form-label fs-6 fw-bolder text-dark">Password</label>
                            <div className="position-relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className={`form-control form-control-lg form-control-solid ${errors.password ? 'is-invalid' : ''}`}
                                    placeholder="Enter your password"
                                    {...register("password", {required: "Password is required"})}
                                />
                                <span
                                    className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                  <i className={`bi bi-eye${showPassword ? '-slash' : ''} fs-2`}></i>
                </span>
                                {errors.password && (
                                    <div className="invalid-feedback">{errors.password.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="rememberMe"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="rememberMe">
                                    Remember me
                                </label>
                            </div>
                            <Link to="/forgot-password" className="link-primary text-decoration-none">
                                Forgot Password?
                            </Link>
                        </div>


                        <div className="d-grid mb-10">
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span className="d-flex justify-content-center align-items-center">
                                        <span className="spinner-border spinner-border-sm me-2" role="status"
                                              aria-hidden="true"></span>
                                        Please wait ...
                                      </span>
                                ) : (
                                    "Continue"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Login